import {Observable} from "rxjs";
import {map, take} from "rxjs/operators";
import {Injectable} from "@angular/core";
import {Apollo} from "apollo-angular";
import {CheckoutUtil} from "../../checkout-util";
import {UsanaObject} from "../../../modules/usana-object";
import {LoggerService} from "../../../modules/common/logger.service";
import {PaymentMethodInput, PaymentMethodsResponse} from "../../models/payment/payment-method";
import {IOrderPaymentResult, IOrderRepaymentQuote} from "../../../modules/orders/orders-models";
import {GetOrderPaymentMethods, GetOrderRepaymentQuote, ReplaceOrderPayment} from "./gql-order-payment";

export interface IReplaceOrderPaymentInput {
    orderId: number;
    paymentMethod: PaymentMethodInput;
}

@Injectable({
    providedIn: 'root'
})
export class GqlOrderPaymentService extends UsanaObject {

    constructor(loggerService: LoggerService,
                private apollo: Apollo
    ) {
        super(loggerService);
    }

    // Loads the payment methods available for an existing (pending) order, filtered by the order's locale/currency.
    getOrderPaymentMethods(orderId: number): Observable<PaymentMethodsResponse> {
        return this.apollo.watchQuery({
            query: GetOrderPaymentMethods,
            variables: {orderId},
            fetchPolicy: 'no-cache'
        }).valueChanges.pipe(take(1), map((data: any) =>
            CheckoutUtil.handleApolloResponse(data, (d) => d.data.getPlacedOrder.paymentMethodsResponse)));
    }

    // What the order still owes, as payment-service computes it. Read-only -- releases nothing.
    getOrderRepaymentQuote(orderId: number): Observable<IOrderRepaymentQuote> {
        return this.apollo.watchQuery({
            query: GetOrderRepaymentQuote,
            variables: {orderId},
            fetchPolicy: 'no-cache'
        }).valueChanges.pipe(take(1), map((data: any) =>
            CheckoutUtil.handleApolloResponse(data, (d) => d.data.getOrderRepaymentQuote)));
    }

    // Releases the payments still live on a pending order and charges the remainder to the chosen method.
    replaceOrderPayment(input: IReplaceOrderPaymentInput): Observable<IOrderPaymentResult> {
        return this.apollo.mutate({
            mutation: ReplaceOrderPayment,
            fetchPolicy: 'no-cache',
            variables: {input}
        }).pipe(take(1), map((response: any) =>
            CheckoutUtil.handleApolloResponse(response, (d) => d.data.replaceOrderPayment)));
    }
}
